/**
 * PastCycleDetailsScreen
 *
 * Collects details about past fertility treatments:
 * type, count, and outcome for each.
 */

import React, { useState } from "react";
import { View, Text, Pressable, ScrollView } from "react-native";
import { Plus, X } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";
import type { PastCycle, PastCycleType, PastCycleOutcome } from "../types";

const CYCLE_TYPES: { id: PastCycleType; labelKey: string }[] = [
  { id: "ivf", labelKey: "plan_builder.past_cycle_details.type.ivf" },
  { id: "iui", labelKey: "plan_builder.past_cycle_details.type.iui" },
  {
    id: "egg-freezing",
    labelKey: "plan_builder.past_cycle_details.type.egg_freezing",
  },
  {
    id: "embryo-freezing",
    labelKey: "plan_builder.past_cycle_details.type.embryo_freezing",
  },
  {
    id: "medicated-cycle",
    labelKey: "plan_builder.past_cycle_details.type.medicated_cycle",
  },
  { id: "other", labelKey: "plan_builder.past_cycle_details.type.other" },
];

const OUTCOMES: { id: PastCycleOutcome; labelKey: string }[] = [
  {
    id: "successful",
    labelKey: "plan_builder.past_cycle_details.outcome.successful",
  },
  {
    id: "unsuccessful",
    labelKey: "plan_builder.past_cycle_details.outcome.unsuccessful",
  },
  {
    id: "cancelled",
    labelKey: "plan_builder.past_cycle_details.outcome.cancelled",
  },
  {
    id: "ongoing",
    labelKey: "plan_builder.past_cycle_details.outcome.ongoing",
  },
  {
    id: "prefer-not-to-say",
    labelKey: "plan_builder.past_cycle_details.outcome.prefer_not_to_say",
  },
];

type Props = {
  onNext: () => void;
};

export default function PastCycleDetailsScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PastCycleDetailsScreen");
  const { state, updateState } = usePlanBuilder();
  const [cycles, setCycles] = useState<PastCycle[]>(
    state.pastCycles.length > 0
      ? state.pastCycles
      : [{ type: "ivf", count: 1, outcomes: [] }],
  );

  const updateCycle = (index: number, partial: Partial<PastCycle>) => {
    const updated = cycles.map((c, i) => {
      if (i !== index) return c;
      const merged = { ...c, ...partial };
      // Keep outcomes array in sync with count
      if (merged.outcomes.length > merged.count) {
        merged.outcomes = merged.outcomes.slice(0, merged.count);
      }
      return merged;
    });
    setCycles(updated);
    updateState({ pastCycles: updated });
  };

  const updateOutcome = (
    cycleIndex: number,
    outcomeIndex: number,
    outcome: PastCycleOutcome,
  ) => {
    const updated = cycles.map((c, i) => {
      if (i !== cycleIndex) return c;
      const outcomes = [...c.outcomes];
      outcomes[outcomeIndex] = outcome;
      return { ...c, outcomes };
    });
    setCycles(updated);
    updateState({ pastCycles: updated });
  };

  const addCycle = () => {
    const updated = [
      ...cycles,
      { type: "ivf" as PastCycleType, count: 1, outcomes: [] },
    ];
    setCycles(updated);
    updateState({ pastCycles: updated });
  };

  const removeCycle = (index: number) => {
    const updated = cycles.filter((_, i) => i !== index);
    setCycles(updated);
    updateState({ pastCycles: updated });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.past_cycle_details.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.past_cycle_details.subtitle")}
      </Text>

      {cycles.map((cycle, index) => (
        <View
          key={index}
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.82)",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.9)",
            padding: 16,
            marginBottom: 12,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.04,
            shadowRadius: 8,
          }}
        >
          {/* Header with remove button */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.textLight,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {t("plan_builder.past_cycle_details.cycle_n", {
                params: { n: String(index + 1) },
              })}
            </Text>
            {cycles.length > 1 && (
              <Pressable
                onPress={() => {
                  // Icon-only control with no visible copy to name it.
                  capturePress(pressLabel("remove_past_cycle"));
                  removeCycle(index);
                }}
              >
                <X size={18} color={EMBER.colors.textFaint} />
              </Pressable>
            )}
          </View>

          {/* Type selection */}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: EMBER.colors.textMid,
              marginBottom: 8,
            }}
          >
            {t("plan_builder.past_cycle_details.type_label")}
          </Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 14,
            }}
          >
            {CYCLE_TYPES.map((ct) => {
              const isSelected = cycle.type === ct.id;
              return (
                <Pressable
                  key={ct.id}
                  onPress={() => {
                    capturePress(pressLabel(t(ct.labelKey)), ct.id);
                    updateCycle(index, { type: ct.id });
                  }}
                >
                  <View
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 7,
                      borderRadius: 8,
                      backgroundColor: isSelected
                        ? `${EMBER.colors.text}15`
                        : `${EMBER.colors.textFaint}12`,
                      borderWidth: 1,
                      borderColor: isSelected
                        ? EMBER.colors.text
                        : "transparent",
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: isSelected ? "600" : "400",
                        color: isSelected
                          ? EMBER.colors.text
                          : EMBER.colors.textMid,
                      }}
                    >
                      {t(ct.labelKey)}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>

          {/* Count */}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: EMBER.colors.textMid,
              marginBottom: 8,
            }}
          >
            {t("plan_builder.past_cycle_details.count_label")}
          </Text>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 16,
              marginBottom: 14,
            }}
          >
            <Pressable
              onPress={() => {
                capturePress(pressLabel("−"), "decrease_past_cycle_count");
                updateCycle(index, { count: Math.max(1, cycle.count - 1) });
              }}
            >
              <View
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: `${EMBER.colors.textFaint}15`,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 18,
                    fontWeight: "600",
                    color: EMBER.colors.textMid,
                  }}
                >
                  −
                </Text>
              </View>
            </Pressable>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 20,
                fontWeight: "700",
                color: EMBER.colors.text,
                minWidth: 24,
                textAlign: "center",
              }}
            >
              {cycle.count}
            </Text>
            <Pressable
              onPress={() => {
                capturePress(pressLabel("+"), "increase_past_cycle_count");
                updateCycle(index, { count: cycle.count + 1 });
              }}
            >
              <View
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: `${EMBER.colors.text}15`,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 18,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  +
                </Text>
              </View>
            </Pressable>
          </View>

          {/* Per-cycle outcomes */}
          {Array.from({ length: cycle.count }).map((_, ci) => {
            const typeKey = CYCLE_TYPES.find(
              (ct) => ct.id === cycle.type,
            )?.labelKey;
            const typeLabel = typeKey
              ? t(typeKey)
              : t("plan_builder.past_cycle_details.cycle_n", {
                  params: { n: String(ci + 1) },
                });
            return (
              <View
                key={ci}
                style={{ marginBottom: ci < cycle.count - 1 ? 10 : 0 }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "500",
                    color: EMBER.colors.textMid,
                    marginBottom: 8,
                  }}
                >
                  {cycle.count === 1
                    ? t("plan_builder.past_cycle_details.outcome_label")
                    : t("plan_builder.past_cycle_details.outcome_n_label", {
                        params: { type: typeLabel, n: String(ci + 1) },
                      })}
                </Text>
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}
                >
                  {OUTCOMES.map((o) => {
                    const isSelected = cycle.outcomes[ci] === o.id;
                    return (
                      <Pressable
                        key={o.id}
                        onPress={() => {
                          capturePress(pressLabel(t(o.labelKey)), o.id);
                          updateOutcome(index, ci, o.id);
                        }}
                      >
                        <View
                          style={{
                            paddingHorizontal: 12,
                            paddingVertical: 7,
                            borderRadius: 8,
                            backgroundColor: isSelected
                              ? `${EMBER.colors.text}15`
                              : `${EMBER.colors.textFaint}12`,
                            borderWidth: 1,
                            borderColor: isSelected
                              ? EMBER.colors.text
                              : "transparent",
                          }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 13,
                              fontWeight: isSelected ? "600" : "400",
                              color: isSelected
                                ? EMBER.colors.text
                                : EMBER.colors.textMid,
                            }}
                          >
                            {t(o.labelKey)}
                          </Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </View>
      ))}

      {/* Add another cycle */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("plan_builder.past_cycle_details.add_another")),
            "add_past_cycle",
          );
          addCycle();
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            paddingVertical: 12,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: EMBER.colors.border,
            borderStyle: "dashed",
            marginBottom: 24,
          }}
        >
          <Plus size={16} color={EMBER.colors.textLight} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              fontWeight: "500",
              color: EMBER.colors.textLight,
            }}
          >
            {t("plan_builder.past_cycle_details.add_another")}
          </Text>
        </View>
      </Pressable>

      {/* Continue */}
      <WarmButton title={t("plan_builder.continue")} onPress={onNext} />
    </ScrollView>
  );
}
